import React, { useState, useEffect, useRef } from 'react';
import API_URL from '../apiConfig';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import AnimatedBackground from '../components/AnimatedBackground';
import DocumentTemplate from '../components/DocumentTemplate';
import './FinalDocument.css';

const formatDate = (timestamp) => {
  if (!timestamp) return "N/A";
  const date = new Date(timestamp);
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const FinalDocument = () => {
  const [transcription, setTranscription] = useState("");
  const [summary, setSummary] = useState("");
  const [audioUrl, setAudioUrl] = useState("");
  const [concern, setConcern] = useState("");
  const [actionTaken, setActionTaken] = useState("");
  const [outcome, setOutcome] = useState("");
  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(true);
  const [sessionDate, setSessionDate] = useState("");
  const [venue, setVenue] = useState("N/A");
  const [period, setPeriod] = useState("N/A");
  const [teacherInfo, setTeacherInfo] = useState(null);
  const [studentInfo, setStudentInfo] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const audioRef = useRef(null);
  const documentRef = useRef(null);

  const queryParams = new URLSearchParams(window.location.search);
  const sessionID = queryParams.get('sessionID');

  const handlePrint = () => {
    if (documentRef.current) {
      const clonedElement = documentRef.current.cloneNode(true);
      clonedElement.style.transform = 'none';
      clonedElement.style.boxShadow = 'none';
      
      const printStyles = `
        @page { size: 8.5in 11in; margin: 0; }
        body { margin: 0; padding: 0; }
        * { 
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
        td[style*="background-color: #cfe2f3"],
        tr td[style*="background-color: rgb(207, 226, 243)"] {
          background-color: #cfe2f3 !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
      `;
      
      const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
        .map(node => node.outerHTML)
        .join('');

      const printWindow = window.open('', '_blank');
      printWindow.document.open();
      printWindow.document.write(`
        <html>
          <head>
            <title>Print/Save Document</title>
            ${styles}
            <style>${printStyles}</style>
          </head>
          <body>
            ${clonedElement.outerHTML}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    }
  };

  useEffect(() => {
    // Check user role and ID from localStorage
    const userEmail = localStorage.getItem('userEmail');
    const userRole = localStorage.getItem('userRole');
    const userId = localStorage.getItem('userId');
    
    console.log('LocalStorage data:', {
      userEmail,
      userRole,
      userId
    });
    
    // Use localStorage data if available and valid
    if (userRole && userId) {
      console.log('Using localStorage data');
      setUserRole(userRole);
      setCurrentUserId(userId);
    } else if (userEmail) {
      // Fallback to API call if localStorage data is missing
      console.log('Fetching user data from API...');
      fetch(`${API_URL}/user/get_user_by_email?email=${userEmail}`)
        .then(response => {
          console.log('API response status:', response.status);
          return response.json();
        })
        .then(userData => {
          console.log('User data fetched:', userData);
          if (userData && userData.role) {
            setUserRole(userData.role);
            setCurrentUserId(userData.id_number);
            // Store in localStorage for next time
            localStorage.setItem('userRole', userData.role);
            localStorage.setItem('userId', userData.id_number);
          } else {
            console.error('Invalid user data structure:', userData);
          }
        })
        .catch(error => {
          console.error('Error fetching user data:', error);
        });
    }
  }, []);

  useEffect(() => {
    const fetchSessionData = async () => {
      try {
        if (!sessionID) {
          console.error("No sessionID provided");
          setLoading(false);
          return;
        }
        const response = await fetch(`${API_URL}/consultation/get_final_document?sessionID=${sessionID}`);
        const data = await response.json();
        if (response.ok) {          const cleanString = str => str && typeof str === 'string' ? str.replace(/^"|"$/g, "") : "";
          setTranscription(cleanString(data.transcription || ""));
          setSummary(cleanString(data.summary || ""));
            // Improved audio URL handling - create full URL if path is relative
          const audioPath = data.audio_url || data.audio_file_path || "";
          const fullAudioUrl = audioPath && !audioPath.startsWith('http') && !audioPath.startsWith('/uploads') 
            ? `/uploads/${audioPath.split('/').pop()}` 
            : audioPath;
          setAudioUrl(fullAudioUrl);
          
          setConcern(cleanString(data.concern || ""));
          setActionTaken(cleanString(data.action_taken || ""));
          setOutcome(cleanString(data.outcome || ""));
          setRemarks(cleanString(data.remarks || ""));
          setSessionDate(data.session_date || "");
          setVenue(data.venue || "N/A");
          setPeriod(data.period || "N/A");          // Use teacher_info directly if available, otherwise fall back to teacher_id fetch
          if (data.teacher_info) {
            // Map data structure as needed for proper rendering
            const formattedTeacherInfo = {
              ...data.teacher_info,
              firstName: data.teacher_info.full_name ? data.teacher_info.full_name.split(' ')[0] : '',
              lastName: data.teacher_info.full_name ? 
                data.teacher_info.full_name.split(' ').slice(1).join(' ') : ''
            };
            setTeacherInfo(formattedTeacherInfo);
          } else if (data.teacher_id) {
            const teacherId = data.teacher_id.split('/').pop();
            const teacherResponse = await fetch(`${API_URL}/user/get_user?id=${teacherId}`);
            setTeacherInfo(await teacherResponse.json());
          }          // Use students_info directly if available, otherwise fall back to student_info or student_ids
          if (data.students_info && Array.isArray(data.students_info)) {
            // Format each student record for proper rendering
            const formattedStudentInfo = data.students_info.map(student => ({
              ...student,
              firstName: student.full_name ? student.full_name.split(' ')[0] : '',
              lastName: student.full_name ? student.full_name.split(' ').slice(1).join(' ') : '',
              year_section: student.year_section || ''
            }));
            setStudentInfo(formattedStudentInfo);
            console.log('Student info set (formatted):', formattedStudentInfo);
          } else if (data.student_info && Array.isArray(data.student_info)) {
            setStudentInfo(data.student_info);
            console.log('Student info set (direct):', data.student_info);
          } else if (data.student_ids && Array.isArray(data.student_ids)) {
            const studentPromises = data.student_ids.map(async (studentPath) => {
              const studentId = studentPath.split('/').pop();
              const studentResponse = await fetch(`${API_URL}/user/get_user?id=${studentId}`);
              return studentResponse.json();
            });
            const studentData = await Promise.all(studentPromises);
            setStudentInfo(studentData);
            console.log('Student info set (fetched):', studentData);
          }
        } else {
          console.error("Error fetching session details:", data.error);
        }
      } catch (error) {
        console.error("Error fetching session:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSessionData();
  }, [sessionID]);


  // Calculate initial scale to fit the document in the viewport
  const [initialScale, setInitialScale] = useState(0.35);
  const containerRef = useRef(null);

  // Store resetTransform function
  const resetTransformRef = useRef(null);

  // Effect to trigger reset on mount
  useEffect(() => {
    if (resetTransformRef.current) {
      setTimeout(() => {
        resetTransformRef.current();
        setTimeout(() => resetTransformRef.current(), 50);
      }, 500); // Increased delay to ensure component is fully mounted
    }
  }, [loading]); // Added loading dependency to trigger after data loads

  useEffect(() => {
    const updateScale = () => {
      if (containerRef.current) {
        const containerHeight = containerRef.current.clientHeight;
        const documentHeight = 11 * 96; // 11 inches in pixels (96 DPI)
        const scale = Math.max(0.35, Math.min(2, (containerHeight * 0.95) / documentHeight));
        setInitialScale(scale);
      }
    };

    // Initial update
    updateScale();

    // Add resize listener
    const resizeObserver = new ResizeObserver((entries) => {
      // Debounce the scale update
      if (window.resizeTimeout) {
        clearTimeout(window.resizeTimeout);
      }
      window.resizeTimeout = setTimeout(() => {
        updateScale();
      }, 100);
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Cleanup
    return () => {
      if (window.resizeTimeout) {
        clearTimeout(window.resizeTimeout);
      }
      resizeObserver.disconnect();
    };
  }, []);

  // New: Helper function to format the transcription
  const formatTranscription = (text) => {
    if (!text) return "";
    return text
      .replace(/Teacher:/g, "\nTeacher:")
      .replace(/Student 1:/g, "\nStudent 1:")
      .replace(/Student 2:/g, "\nStudent 2:")
      .replace(/Student 3:/g, "\nStudent 3:")
      .replace(/Student 4:/g, "\nStudent 4:")
      .replace(/Student 5:/g, "\nStudent 5:")
      .replace(/Student:/g, "\nStudent:")
      .trim();
  };



  return (
    <div className="relative min-h-screen overflow-y-auto fade-in">
      <AnimatedBackground />
      {loading ? (
        <div className="flex h-screen items-center justify-center">Loading...</div>
      ) : (
        <div className="relative z-10 min-h-screen p-4 md:p-8 flex flex-col">
          <h1 className="text-2xl md:text-3xl font-bold text-[#057DCD] mb-4 md:mb-6 text-center fade-in delay-100">Final Document</h1>
          <div className="flex flex-col md:flex-row gap-4 md:gap-8 flex-1 mb-4 md:mb-6">
            <div className="w-full md:w-1/2 flex flex-col">
              {/* Left Column: Displaying Summary, Transcription, etc. */}
              <div className="bg-[#057DCD] rounded-lg p-4 md:p-6 flex flex-col fade-in delay-200 md:h-[calc(100vh-12rem)]">
                <div className="flex-1 overflow-y-auto pr-4 final-document-scroll">
                  <section className="mb-6">
                    <h2 className="text-xl font-semibold text-white mb-2">Summary</h2>
                    <div className="p-4 bg-white rounded-lg">
                      {summary || "Summary is not available."}
                    </div>
                  </section>
                  <section className="mb-6">
                    <h2 className="text-xl font-semibold text-white mb-2">Transcription</h2>
                    {/* New: Use pre to preserve line breaks from formatted transcription */}
                    <pre className="p-4 bg-white rounded-lg whitespace-pre-wrap">
                      {formatTranscription(transcription) || "Transcription is not available."}
                    </pre>
                  </section>
                  <section className="mb-6">
                    <h2 className="text-xl font-semibold text-white mb-2">Concern</h2>
                    <div className="p-4 bg-white rounded-lg">
                      {concern || "Concern is not available."}
                    </div>
                  </section>
                  <section className="mb-6">
                    <h2 className="text-xl font-semibold text-white mb-2">Action Taken</h2>
                    <div className="p-4 bg-white rounded-lg">
                      {actionTaken || "Action Taken is not available."}
                    </div>
                  </section>
                  <section className="mb-6">
                    <h2 className="text-xl font-semibold text-white mb-2">Outcome</h2>
                    <div className="p-4 bg-white rounded-lg">
                      {outcome || "Outcome is not available."}
                    </div>
                  </section>
                  <section className="mb-6">
                    <h2 className="text-xl font-semibold text-white mb-2">Remarks</h2>
                    <div className="p-4 bg-white rounded-lg">
                      {remarks || "Remarks is not available."}
                    </div>
                  </section>
                </div>
              </div>
            </div>
            {/* Right Column: Document Preview */}
            <div className="w-full md:w-1/2 bg-white rounded-lg shadow-lg p-4 md:p-6 flex flex-col fade-in delay-300 md:h-[calc(100vh-12rem)] h-[calc(100vh-12rem)] mb-24 md:mb-0">
              <TransformWrapper
                initialScale={initialScale}
                minScale={0.35}
                maxScale={2}
                initialPositionX={265}
                initialPositionY={345}
                limitToBounds={false}
                centerOnInit={true}
                centerZoomedOut={true}
                onTransformed={(e) => {
                  // Clear any existing animation frame
                  if (window.transformTimeout) {
                    clearTimeout(window.transformTimeout);
                  }
                  // Debounce transform updates
                  window.transformTimeout = setTimeout(() => {
                    if (containerRef.current) {
                      containerRef.current.style.willChange = 'auto';
                    }
                  }, 100);
                  
                  if (containerRef.current) {
                    containerRef.current.style.willChange = 'transform';
                  }
                }}
              >
                {({ zoomIn, zoomOut, resetTransform }) => {
                  // Store resetTransform function in ref for auto-reset on mount
                  resetTransformRef.current = resetTransform;
                  
                  return (
                  <>
                    <div className="mb-4 flex justify-end space-x-2">
                      <button
                        onClick={() => zoomOut()}
                        className="bg-[#057DCD] text-white px-4 py-2 md:px-3 md:py-1 rounded-lg hover:bg-[#54BEFF] text-lg md:text-base"
                      >
                        -
                      </button>
                      <button
                        onClick={() => {
                          resetTransform();
                          setTimeout(() => resetTransform(), 50);
                        }}
                        className="bg-[#057DCD] text-white px-4 py-2 md:px-3 md:py-1 rounded-lg hover:bg-[#54BEFF] text-lg md:text-base"
                      >
                        Reset
                      </button>
                      <button
                        onClick={() => zoomIn()}
                        className="bg-[#057DCD] text-white px-4 py-2 md:px-3 md:py-1 rounded-lg hover:bg-[#54BEFF] text-lg md:text-base"
                      >
                        +
                      </button>
                    </div>
                    <div
                      ref={containerRef}
                      className="flex-1 overflow-hidden bg-gray-100 rounded-lg flex items-center justify-center"
                      style={{ contain: 'paint layout size' }}
                    >
                      <TransformComponent
                        wrapperClass="w-full h-full flex items-center justify-center"
                        contentClass="flex items-center justify-center transform-center"
                      >
                        <div ref={documentRef}>
                          <DocumentTemplate
                            sessionDate={sessionDate}
                            venue={venue}
                            period={period}
                            concern={concern}
                            actionTaken={actionTaken}
                            outcome={outcome}
                            remarks={remarks}
                            summary={summary}
                            teacherInfo={teacherInfo}
                            studentInfo={studentInfo}
                          />
                        </div>
                      </TransformComponent>
                    </div>
                  </>
                  );
                }}
              </TransformWrapper>
            </div>
          </div>
          {/* Controls */}
          <div className="fixed md:relative bottom-0 left-0 right-0 bg-white md:bg-transparent p-4 shadow-lg md:shadow-none rounded-t-lg md:rounded-lg flex flex-col md:flex-row items-center gap-4 md:gap-0 md:justify-between z-50 pb-safe">
            <div className="w-full md:w-1/2 max-w-full">              {audioUrl ? (
                <audio 
                  ref={audioRef} 
                  controls 
                  src={audioUrl.startsWith('http') 
                       ? audioUrl 
                       : audioUrl.startsWith('/') 
                         ? `${API_URL}${audioUrl}` 
                         : `${API_URL}/uploads/${audioUrl}`} 
                  className="w-full fade-in delay-400"
                  onError={(e) => {
                    console.error("Audio loading error:", e);
                    // Try alternative URL format if the first one fails
                    if (audioUrl.includes('/')) {
                      e.target.src = `${API_URL}/uploads/${audioUrl.split('/').pop()}`;
                    }
                  }}
                >
                  Your browser does not support the audio element.
                </audio>
              ) : (
                <p className="text-gray-600">No audio recording available</p>
              )}
            </div>
            <div>     
              <button
                onClick={handlePrint}
                className="bg-[#057DCD] hover:bg-[#54BEFF] text-white px-4 py-2 rounded-lg transition-colors fade-in delay-200 w-full md:w-auto text-center"
              >
                Print/Save Document
              </button>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
};

export default FinalDocument;
